import { Stack } from '@sureco/design-system'
import { Button, Input, Table } from '@surecompanies/core_components'
import { useRouterContext } from 'components/RouterContext'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { isDemoCustomer } from 'enrollment-platform/pages/DemoCompanyManagement/DemoCustomer/utils'
import { useDebounce } from 'hooks/useDebounce'
import Page from 'layouts/Page'
import { isNil } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import { CUSTOMER_MANAGEMENT, NEW_CUSTOMER } from '../../paths'
import { demoCompanyViewPath } from 'enrollment-platform/pages/DemoCompanyManagement/paths'
import useCanAccessDemoCompanies from 'enrollment-platform/pages/DemoCompanyManagement/useCanAccessDemoCompanies'
import { DEFAULT_PAGINATION_LIMIT } from './constants'
import useListCustomer, { SortBy, Sorter } from './hooks/useListCustomer'
import './ListCustomer.scss'

export default function ListCustomer() {
  const { router } = useRouterContext()
  const canAccessDemo = useCanAccessDemoCompanies()

  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PAGINATION_LIMIT,
    total: 0,
  })

  // Demo companies appear in this list like any other customer, but the demo
  // section is platform-admin only and flag-gated. Anyone who can't get there
  // falls back to the regular customer view rather than being bounced.
  const viewPathFor = (record: { id: string; name: string }) =>
    isDemoCustomer(record) && canAccessDemo
      ? `/${demoCompanyViewPath(record.id)}`
      : `/${CUSTOMER_MANAGEMENT}/${record.id}/view`

  const { dataSource, columns, isLoading, total, filter, setFilter } =
    useListCustomer({
      onView: (record) => router.push(viewPathFor(record)),
      onEdit: (record) => {
        router.push(`/${CUSTOMER_MANAGEMENT}/${record.id}/edit`)
      },
      page: pagination?.page,
      limit: pagination?.limit,
    })
  const [searchName, setSearchName] = useState(filter?.name)
  const debouncedSearchName = useDebounce(searchName, 300)

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchName(value)
  }, [])

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      name: debouncedSearchName,
    }))
  }, [debouncedSearchName, setFilter])

  return (
    <Page
      title={'Customer Management'}
      action={
        <Button
          type="primary"
          disabled={isLoading}
          onClick={() => browserHistory.push(NEW_CUSTOMER)}
        >
          Add New Customer
        </Button>
      }
      breadcrumb={
        <BreadcrumbNav
          items={[{ title: 'Home', path: `/` }, { title: 'Customers' }]}
        />
      }
    >
      <div className="search-input-container">
        <Stack direction="row" space={10} className="search-input-wrapper">
          <Input
            placeholder="Search by company name"
            value={searchName}
            allowClear
            onChange={handleSearch}
            onClear={() => {
              setSearchName('')
              setFilter((prev) => ({
                ...prev,
                name: '',
              }))
            }}
          />
          <div className="total-text-wrapper">Total: {total ?? '--'}</div>
        </Stack>
      </div>
      <Table
        dataSource={dataSource}
        columns={columns as any}
        rowKey={(record) => record.id}
        loading={isLoading}
        locale={{
          emptyText: 'No Data',
        }}
        sortDirections={['ascend', 'descend']}
        rowClassName={(record) =>
          isDemoCustomer(record) ? 'demo-customer-row' : ''
        }
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('button')) {
              return
            }
            router.push(viewPathFor(record))
          },
          style: { cursor: 'pointer' },
        })}
        onChange={(pagination, filters, sorter) => {
          const getSortOrder = () => {
            if (!(sorter as unknown as Sorter)?.order) return undefined

            return (sorter as unknown as Sorter).order === 'ascend'
              ? 'asc'
              : 'desc'
          }

          setPagination((prev) => ({
            ...prev,
            page: pagination.current ?? prev.page,
            limit: pagination.pageSize ?? prev.limit,
          }))
          setFilter((prev) => ({
            ...prev,
            status: isNil(filters.status)
              ? undefined
              : (filters.status as string[]),
            sortBy: (sorter as unknown as Sorter)?.field as SortBy,
            sortOrder: getSortOrder(),
          }))
        }}
        pagination={{
          total: total,
          pageSize: pagination?.limit,
          current: pagination?.page,
          pageSizeOptions: [25, 50, 100, 200, 300],
          onShowSizeChange: (page, limit) => {
            setPagination((prev) => ({
              ...prev,
              page,
              limit,
            }))
          },
          onChange: (page, limit) => {
            setPagination((prev) => ({
              ...prev,
              page,
              limit,
            }))
          },
        }}
      />
    </Page>
  )
}
